import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Flex, Input, useDisclosure, useToast } from '@chakra-ui/react';
import { ProfessoresGet } from '../Professores';
import { deleteProfessor, updateProfessor } from '../../services/api';
import { CampoForm } from '../../components/CampoForm';
import { ConfirmarExclusao } from '../../components/ConfirmarExclusao';
import { DrawerForm } from '../../components/DrawerForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { emailInvalido, vazio } from '../../utils/validacao';
import { CampoDocumento } from '../../components/CampoDocumento';
import { TipoDocumento, erroDocumento } from '../../utils/documento';

type DrawerCreateProps = {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
	afterClose: () => void;
	professor: ProfessoresGet;
};

type Campo = 'nome' | 'sobrenome' | 'documento' | 'email';

export function DrawerUpdate({ isOpen, onClose, afterClose, professor }: DrawerCreateProps) {
	const toast = useToast();
	const confirmacao = useDisclosure();
	const { erros, validar, limparErro, limparTudo } = useErrosForm<Campo>();

	const [ loading, setLoading ] = useState(false);

	const [ nome, setNome ] = useState<string>('');
	const [ sobrenome, setSobrenome ] = useState<string>('');
	const [ documento, setDocumento ] = useState<string>('');
	const [ tipoDocumento, setTipoDocumento ] = useState<TipoDocumento>('CPF');
	const [ email, setEmail ] = useState<string>('');

	// Descarta as edicoes voltando aos dados do professor, e nao para campos
	// vazios: o pai reaproveita o mesmo objeto ao reabrir a mesma linha, entao
	// limpar aqui deixaria o formulario em branco na proxima abertura.
	const resetForm = useCallback(
		() => {
			setDocumento(professor.documento);
			setTipoDocumento(professor.tipoDocumento);
			setEmail(professor.email);
			setNome(professor.nome);
			setSobrenome(professor.sobrenome);
			limparTudo();
		},
		[ professor, limparTudo ]
	);

	useEffect(
		() => {
			if (isOpen) resetForm();
		},
		[ isOpen, resetForm ]
	);

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (vazio(nome)) encontrados.nome = 'Nome é obrigatório!';
		if (vazio(sobrenome)) encontrados.sobrenome = 'Sobrenome é obrigatório!';

		const erroDoDocumento = erroDocumento(tipoDocumento, documento);
		if (erroDoDocumento) encontrados.documento = erroDoDocumento;

		if (vazio(email)) {
			encontrados.email = 'Email é obrigatório!';
		} else if (emailInvalido(email)) {
			encontrados.email = 'Informe um email válido.';
		}

		return encontrados;
	};

	const handleSalvar = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!validar(montarErros())) return;

		setLoading(true);
		updateProfessor(professor.email, {
			documento: documento.trim(),
			tipoDocumento,
			email: email.trim(),
			nome: nome.trim(),
			sobrenome: sobrenome.trim()
		})
			.then(() => {
				setLoading(false);
				toast({
					title: `Professor(a) atualizado com sucesso.`,
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				if (!error.response) return;
				const message = error.response.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível atualizar professor(a).`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleExcluir = () => {
		confirmacao.onClose();
		setLoading(true);
		deleteProfessor(professor.email)
			.then(() => {
				setLoading(false);
				toast({
					title: `Professor(a) removido(a) com sucesso.`,
					status: 'success',
					isClosable: true
				});
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				if (!error.response) return;
				const message = error.response.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível remover professor(a).`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const handleFechar = () => {
		resetForm();
		onClose();
		afterClose();
	};

	return (
		<>
			<DrawerForm
				isOpen={isOpen}
				onClose={handleFechar}
				titulo="Editar Professor"
				formId="form-editar-professor"
				loading={loading}
				onSubmit={handleSalvar}
				acaoDestrutiva={
					<Button colorScheme="red" variant="outline" isDisabled={loading} onClick={confirmacao.onOpen}>
						Excluir
					</Button>
				}
			>
				<Flex gap="16px" flexDir={{ base: 'column', sm: 'row' }}>
					<CampoForm label="Nome" htmlFor="nome" erro={erros.nome}>
						<Input
							id="nome"
							placeholder="Insira o Nome"
							autoComplete="given-name"
							onChange={(e) => {
								setNome(e.target.value);
								limparErro('nome');
							}}
							value={nome}
						/>
					</CampoForm>

					<CampoForm label="Sobrenome" htmlFor="sobrenome" erro={erros.sobrenome}>
						<Input
							id="sobrenome"
							placeholder="Insira o Sobrenome"
							autoComplete="family-name"
							onChange={(e) => {
								setSobrenome(e.target.value);
								limparErro('sobrenome');
							}}
							value={sobrenome}
						/>
					</CampoForm>
				</Flex>

				<CampoDocumento
					tipo={tipoDocumento}
					valor={documento}
					onTipoChange={(novo) => {
						setTipoDocumento(novo);
						limparErro('documento');
					}}
					onValorChange={(valor) => {
						setDocumento(valor);
						limparErro('documento');
					}}
					erro={erros.documento}
				/>

				<CampoForm label="Email" htmlFor="email" erro={erros.email}>
					<Input
						id="email"
						placeholder="professor@email.com"
						type="email"
						autoComplete="email"
						onChange={(e) => {
							setEmail(e.target.value);
							limparErro('email');
						}}
						value={email}
					/>
				</CampoForm>
			</DrawerForm>

			<ConfirmarExclusao
				isOpen={confirmacao.isOpen}
				onClose={confirmacao.onClose}
				onConfirmar={handleExcluir}
				titulo="Excluir professor(a)"
				descricao={`Tem certeza que deseja excluir ${professor.nome} ${professor.sobrenome}? Esta ação não pode ser desfeita.`}
			/>
		</>
	);
}
